// frontend/src/pages/ReportScenarios.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Snackbar from '@mui/material/Snackbar';
import DownloadIcon from '@mui/icons-material/Download';

// Import the specialized sections
import { UnmetDemandWaterSystemSection } from './UnmetDemandWaterSystemSection';
import { UnmetDemandPowerSystemSection } from './UnmetDemandPowerSystemSection';
import { UnmetDemandTransportSystemSection } from './UnmetDemandTransportSystemSection';

// Import authFetch wrapper
import { authFetch } from '../authFetch';

// --- Helper Components ---
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`analysis-tabpanel-${index}`} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function UnmetDemandSubTabs({ analysisData, nameMap, floodData, scenarioId, llmSummaries, scenarioName, hazardLabel, selectedSystems =[], onActiveSystemChange }) {
  const calculateInitialTab = useCallback(() => {
    const socialSystems =['health', 'police', 'fire', 'military', 'pharmacy', 'food', 'train', 'school', 'office'];
    const hasSocialSystems = selectedSystems.some(s => socialSystems.includes(s));

    if (selectedSystems.includes('water')) return 0;
    if (selectedSystems.includes('power')) return 1;
    if (hasSocialSystems) return 2;
    return 0;
  }, [selectedSystems]);

  const [subTabValue, setSubTabValue] = useState(calculateInitialTab);
  const handleSubTabChange = (event, newValue) => setSubTabValue(newValue);

  // Tell the parent component when the main tab changes
  useEffect(() => {
    if (subTabValue === 0) onActiveSystemChange('water');
    else if (subTabValue === 1) onActiveSystemChange('power');
    // If it is 2 (Social), TransportAccessSubTabs handles telling the parent which specific one it is
  }, [subTabValue, onActiveSystemChange]);

  const socialSystems =['health', 'police', 'fire', 'military', 'pharmacy', 'food', 'train', 'school', 'office'];
  const hasSocialSystems = selectedSystems.some(s => socialSystems.includes(s));

  return (
    <Box>
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={subTabValue}
          onChange={handleSubTabChange}
          aria-label="unmet demand system types"
          variant="fullWidth"
        >
          <Tab label="Water Supply Shortages" id="unmet-demand-subtab-0" disabled={!selectedSystems.includes('water')} />
          <Tab label="Electricity Supply Shortages" id="unmet-demand-subtab-1" disabled={!selectedSystems.includes('power')} />
          <Tab label="Social Service Supply Restrictions" id="unmet-demand-subtab-2" disabled={!hasSocialSystems} />
        </Tabs>
      </Paper>
      <TabPanel value={subTabValue} index={0}>
        {selectedSystems.includes('water') && <UnmetDemandWaterSystemSection analysisData={analysisData} nameMap={nameMap} floodData={floodData} scenarioId={scenarioId} llmSummaries={llmSummaries} scenarioName={scenarioName} hazardLabel={hazardLabel} />}
      </TabPanel>
      <TabPanel value={subTabValue} index={1}>
        {selectedSystems.includes('power') && <UnmetDemandPowerSystemSection analysisData={analysisData} nameMap={nameMap} floodData={floodData} scenarioId={scenarioId} llmSummaries={llmSummaries} scenarioName={scenarioName} hazardLabel={hazardLabel} />}
      </TabPanel>
      <TabPanel value={subTabValue} index={2}>
        {hasSocialSystems && <TransportAccessSubTabs analysisData={analysisData} nameMap={nameMap} floodData={floodData} scenarioId={scenarioId} llmSummaries={llmSummaries} scenarioName={scenarioName} hazardLabel={hazardLabel} selectedSystems={selectedSystems} onActiveSystemChange={onActiveSystemChange} />}
      </TabPanel>
    </Box>
  );
}

function TransportAccessSubTabs({ analysisData, nameMap, floodData, scenarioId, llmSummaries, scenarioName, hazardLabel, selectedSystems = [], onActiveSystemChange }) {
  const[transportSubTabValue, setTransportSubTabValue] = useState(0);
  const handleTransportSubTabChange = (event, newValue) => setTransportSubTabValue(newValue);

  const allTransportSystems =[
    { key: 'health', label: 'Healthcare' },
    { key: 'police', label: 'Police' },
    { key: 'fire', label: 'Fire station' },
    { key: 'military', label: 'Military' },
    { key: 'pharmacy', label: 'Pharmacy' },
    { key: 'food', label: 'Food retail' },
    { key: 'train', label: 'Train station' },
    { key: 'school', label: 'School' },
    { key: 'office', label: 'Government office' },
  ];

  const availableTransportSystems = allTransportSystems.filter(system => selectedSystems.includes(system.key));

  // Tell the parent component when the social sub-tab changes
  useEffect(() => {
    if (availableTransportSystems[transportSubTabValue]) {
      onActiveSystemChange(availableTransportSystems[transportSubTabValue].key);
    }
  },[transportSubTabValue, selectedSystems, onActiveSystemChange]); // Using selectedSystems to avoid React infinite loops

  return (
    <Box>
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={transportSubTabValue}
          onChange={handleTransportSubTabChange}
          aria-label="transport access system types"
          variant="scrollable"
          scrollButtons="auto"
        >
          {availableTransportSystems.map((system) => (
            <Tab key={system.key} label={system.label} />
          ))}
        </Tabs>
      </Paper>
      {availableTransportSystems.map((system, index) => (
        <TabPanel key={system.key} value={transportSubTabValue} index={index}>
          <UnmetDemandTransportSystemSection
            analysisData={analysisData}
            nameMap={nameMap}
            systemLabel={system.label}
            systemKey={system.key}
            floodData={floodData}
            scenarioId={scenarioId}
            llmSummaries={llmSummaries}
            scenarioName={scenarioName}
            hazardLabel={hazardLabel}
          />
        </TabPanel>
      ))}
    </Box>
  );
}

// --- Export Button ---
function ExportWordButton({ scenarioId, scenarioName, disabled, activeSystem }) {
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar]   = useState({ open: false, message: '', severity: 'success' });

  const handleExport = async () => {
    setExporting(true);
    try {
      // 1. Capture the Mapbox Canvas from the DOM
      let mapImageBase64 = null;
      
      const canvas = document.querySelector('.mapboxgl-canvas') || document.querySelector('canvas');
      
      if (canvas) {
        mapImageBase64 = canvas.toDataURL('image/png');
        console.log("✅ Canvas found! Image string length:", mapImageBase64.length);
      } else {
        console.warn("❌ No canvas found on the page! The map screenshot will be blank.");
      }

      // 2. Change to POST request and send the image AND the active tab
      const response = await authFetch(`/api/scenarios/${scenarioId}/export_report/`, {
        method: 'POST',
        body: JSON.stringify({ 
            map_image: mapImageBase64,
            active_system: activeSystem 
        })
      });

      if (!response.ok) {
        let errMsg = `Export failed (HTTP ${response.status})`;
        try {
          const errData = await response.json();
          if (errData?.error) errMsg = errData.error;
        } catch (_) {}
        throw new Error(errMsg);
      }

      // Trigger browser download from the streamed response
      const blob     = await response.blob();
      const url      = window.URL.createObjectURL(blob);
      const anchor   = document.createElement('a');
      const safeName = (scenarioName || `scenario_${scenarioId}`).replace(/[^a-z0-9_-]/gi, '_');
      
      // Capitalize the active system name and add it to the file name
      const systemTag = activeSystem ? `_${activeSystem.charAt(0).toUpperCase() + activeSystem.slice(1)}` : '';

      anchor.href     = url;
      anchor.download = `CityWise_Report${systemTag}_${safeName}.docx`; 
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);

      setSnackbar({ open: true, message: 'Report downloaded successfully.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Tooltip title={disabled ? 'Analysis must be completed before exporting' : 'Export Word report for the currently active system tab'}>
        <span>
          <Button variant="outlined" size="small" startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />} onClick={handleExport} disabled={disabled || exporting} sx={{ whiteSpace: 'nowrap' }}>
            {exporting ? 'Exporting…' : 'Export Current Tab'}
          </Button>
        </span>
      </Tooltip>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

// --- Main Report Component ---
export default function ReportScenarios() {
  const { scenarioId } = useParams();
  const [analysisData, setAnalysisData] = useState(null);
  const [nameMap, setNameMap]           = useState(null);
  const[floodData, setFloodData]       = useState(null);
  const [llmSummaries, setLlmSummaries] = useState(null);
  const [scenarioInfo, setScenarioInfo] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  
  // Track the active system to send to the backend
  const[activeSystem, setActiveSystem] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!scenarioId) return;
      setLoading(true);
      setError(null);

      try {
        const scenarioDetailsResponse = await authFetch(`/api/scenarios/${scenarioId}/`);
        if (!scenarioDetailsResponse.ok) throw new Error(`Failed to load scenario details: ${scenarioDetailsResponse.statusText}`);
        const scenarioDetails = await scenarioDetailsResponse.json();
        setScenarioInfo(scenarioDetails);

        const dataPromises =[
          authFetch(`/api/scenarios/${scenarioId}/summary/`),
          authFetch('/api/name-map/'),
          authFetch(`/api/scenarios/${scenarioId}/llm_summary/`),
        ];

        if (scenarioDetails.hazard_file) {
          dataPromises.push(authFetch(`/api/scenarios/${scenarioId}/hazard_geojson/`));
        } else {
          dataPromises.push(Promise.resolve({ ok: true, json: () => Promise.resolve(null) }));
        }

        const [analysisResponse, nameMapResponse, llmResponse, floodResponse] = await Promise.all(dataPromises);

        if (!analysisResponse.ok) throw new Error(`Failed to load analysis summary: ${analysisResponse.statusText}`);
        if (!nameMapResponse.ok) throw new Error(`Failed to load name map: ${nameMapResponse.statusText}`);

        const analysis     = await analysisResponse.json();
        const names        = await nameMapResponse.json();
        const floodGeoJson = await floodResponse.json();

        if (llmResponse.ok) {
          const llmData = await llmResponse.json();
          setLlmSummaries(llmData);
        } else {
          console.warn("AI summary not yet available for this scenario.");
          setLlmSummaries(null);
        }

        setAnalysisData(analysis);
        setNameMap(names);
        setFloodData(floodGeoJson);

      } catch (e) {
        console.error('Failed to fetch scenario data:', e);
        setError(`Failed to load scenario report data. ${e.message || e}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [scenarioId]);

  if (loading) {
    return (
      <Container
        maxWidth="xl"
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Scenario Report…</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!analysisData || Object.keys(analysisData).length === 0) {
    return (
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        <Alert severity="warning">No analysis data available for this scenario.</Alert>
      </Container>
    );
  }

  const isCompleted = scenarioInfo?.status === 'completed';

  return (
    <Container maxWidth="xl">
      <Box className="w-full">
        {/* ── Title row with Export button ── */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ my: 3 }}
        >
          <Typography component="h1" variant="h5">
            Systemic Risk Report for Scenario: {scenarioInfo?.name || '…'}
          </Typography>

          <ExportWordButton
            scenarioId={scenarioId}
            scenarioName={scenarioInfo?.name}
            disabled={!isCompleted}
            activeSystem={activeSystem} // <-- Passed down to the button
          />
        </Stack>

        <UnmetDemandSubTabs
          analysisData={analysisData}
          nameMap={nameMap}
          floodData={floodData}
          scenarioId={scenarioId}
          llmSummaries={llmSummaries}
          scenarioName={scenarioInfo?.name}
          hazardLabel={scenarioInfo?.hazard_label}
          selectedSystems={scenarioInfo?.selected_systems || []}
          onActiveSystemChange={setActiveSystem}
        />
      </Box>
    </Container>
  );
}